import './MapPane.css';

import React from 'react';
import L from 'leaflet';
import { Map, Marker, Polyline, Popup, TileLayer, Tooltip } from 'react-leaflet';
import { concat, isArray, isEqual, orderBy } from 'lodash';

import 'leaflet/dist/leaflet.css';
import { UtilsService } from '../services/UtilsService';

import IconCar from '../assets/car.png';
import IconGreen from '../assets/greenmarker.png';
import IconRed from '../assets/redmarker.png';
import IconGrey from '../assets/greymarker.png';


const MarkerCar = new L.Icon({
    iconUrl: IconCar,
    iconSize: [42, 24],
    iconAnchor: [24, 24],
    popupAnchor: [2, -12]
});

const MarkerGreen = new L.Icon({
    iconUrl: IconGreen,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
    tooltipAnchor: [0, 16]
});

const MarkerRed = new L.Icon({
    iconUrl: IconRed,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
    tooltipAnchor: [0, 16]
});

const MarkerGrey = new L.Icon({
    iconUrl: IconGrey,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
    tooltipAnchor: [0, 16]
});


// interface MapPaneProps {
//     activeJobs: Job[];
//     completedJobs: Job[];
//     gpsPings: LatLng[];
//     trackingDetails: TrackingDetails;
//     hideActiveJobMarkers: boolean;
//     hideCompletedJobMarkers: boolean;
//     hideVehicleMarkers: boolean;
//     hideVehiclePath: boolean;
//     hideLegendContainer: boolean;
//     hideLegendSelectedJob: boolean;
//     hideLegendLiveJob: boolean;
//     hideLegendCompletedJob: boolean;
// }


export class MapPane extends React.Component {

    mapRef; /* Store the reference for the Map component */
    utilsService = new UtilsService();


    constructor(props) {
        super(props);
    }


    emitCompletedJobMarkerClicked(jobNumber) {
        if (jobNumber && this.props?.completedJobMarkerClicked) {
            this.props.completedJobMarkerClicked(jobNumber)
        }
    }


    emitLiveJobMarkerClicked(jobNumber) {
        if (this.props?.liveJobMarkerClicked) {
            this.props.liveJobMarkerClicked(jobNumber);
        }
    }


    /**
     * Determine the different properties like center, maxLat, and other properties based on drivers data.
     */
    get driversCoordinateProps() {
        var box = {
            longMin: 0.0,
            longMax: 0.0,
            latMin: 0.0,
            latMax: 0.0,
            longCenter: 0.0,
            latCenter: 0.0
        };

        if (this.props.drivers?.length) {
            this.props.drivers.forEach((element) => {
                if (!box.longMin) {
                    box.longMin = element.lng;
                    box.longMax = element.lng;
                    box.latMin = element.lat;
                    box.latMax = element.lat;
                }

                if (box.longMin > element.lng) box.longMin = element.lng;
                if (box.longMax < element.lng) box.longMax = element.lng;
                if (box.latMin > element.lat) box.latMin = element.lat;
                if (box.latMax < element.lat) box.latMax = element.lat;
            });
        }

        box.longCenter = (box.longMin + box.longMax) / 2;
        box.latCenter = (box.latMin + box.latMax) / 2;

        if (box.longCenter === 0.0) box.longCenter = 144.959721;
        if (box.latCenter === 0.0) box.latCenter = -37.81569;

        return box;
    }


    getBox() {
        var box = this.driversCoordinateProps;
        return [[box.latMin, box.longMin], [box.latMax, box.longMax]];
    }


    getBoxTd = (td) => {
        if(this.isDriverLocationValid(td)) {
            return [
                [td.driver.location.lat - .2, td.driver.location.long - .2],
                [td.driver.location.lat + .2, td.driver.location.long + .2]
            ];
        }

        else {
            const stateCoordinates = this.utilsService.stateCenterCoordinates(td?.details?.stateId);
            return [
                [stateCoordinates.lat - .2, stateCoordinates.lng - .2],
                [stateCoordinates.lat + .2, stateCoordinates.lng + .2]
            ];
        }
    }


    /**
     * Map should be centered by driver's location by default.
     * But there are scenarios where driver's location is a 0,0(meaning it has invalid location) so we are going to center the map using the current state.
     */
    getInitialMapCenter(trackingDetails) {
        if(this.isDriverLocationValid(trackingDetails)) {
            return [trackingDetails?.driver?.location?.lat ?? 0, trackingDetails?.driver?.location?.long ?? 0];
        }

        else {
            const stateCoordinates = this.utilsService.stateCenterCoordinates(trackingDetails?.details?.stateId);
            return [stateCoordinates.lat, stateCoordinates.lng];
        }
    }


    /**
     * Driver's location is invalid if it is 0,0
     */
    isDriverLocationValid(trackingDetails) {
        if(trackingDetails?.driver?.location?.lat || trackingDetails?.driver?.location?.long) {
            return true;
        } else {
            return false;
        }
    }


    centerMapByCoordinates(lat, lng) {
        if(lat && lng) {
            // const currentMapZoom = this.mapRef.leafletElement.getZoom();
            const zoomLevel = 16;
            this.mapRef.leafletElement.flyTo([lat, lng], zoomLevel);
        }
    }


    /**
     * Will try to find the driver marker that matches the provided driverNumber then center the map to it.
     * Will do nothing if no marker matched the provided driverNumber.
     */
    centerMapByDriverNumber(driverNumber) {
        const zoomLevel = 16;
        const driver = this.props.drivers?.find(driver => driver.id === driverNumber);

        if(driver) {
            this.mapRef.leafletElement.flyTo([driver.lat, driver.lng], zoomLevel);
        }
    }


    /**
     * Groups object with same coordinates.
     * @param data Collection of objects that has latitude and longitude properties
     */
    groupByCoordinates(data) {
        const temp = {};
        
        data?.forEach(job => {
            const lat = job.latitude ?? job.lat;
            const long = job.longitude ?? job.long;
            const key = `${lat},${long}`;

            if(isArray(temp[key])) {
                temp[key].push(job);
            } else {
                temp[key] = [job];
            }
        });

        const groupedByCoords = [];
        Object.keys(temp).forEach(key => {
            groupedByCoords.push(temp[key]);
        })

        return groupedByCoords?.filter(dataSet => dataSet.length); /* Filter out empty data sets */
    }


    renderLegendContainer() {
        const legendSelectedJob = <div className="selected flex flex-row gap-2 align-items-center">
            <i className="pi pi-circle-on"></i>
            <div>Selected Job</div>
        </div>;

        const legendLiveJob = <div className="live flex flex-row gap-2 align-items-center">
            <i className="pi pi-circle-on"></i>
            <div>Live Job</div>
        </div>;

        const legendCompletedJob = <div className="completed flex flex-row gap-2 align-items-center">
            <i className="pi pi-circle-on"></i>
            <div>Completed Job</div>
        </div>;
        
        if(this.props?.hideLegendContainer === true) {
            return null;
        }

        else {
            return <div className="legend-container flex flex-column">
                {this.props?.hideLegendSelectedJob ? null : legendSelectedJob}
                {this.props?.hideLegendLiveJob ? null : legendLiveJob}
                {this.props?.hideLegendCompletedJob ? null : legendCompletedJob}
            </div>
        }
    }


    renderRouteCodeDetailsInMarker(leg) {
        if(leg.showRouteCode) {
            return <>
                <div className="flex flex-row gap-1"><b>Route</b><span>{leg.routeCode}</span></div>
                <div className="flex flex-row gap-1"><b>Drop</b><span>{leg.dropNumber}</span></div>
            </>
        }
    }


    // #region Methods for rendering markers and paths
    renderDriverMarker(trackingDetails) {
        if(trackingDetails?.driver?.location?.lat && trackingDetails?.driver?.location?.long) {
            return <Marker position={[trackingDetails.driver.location.lat, trackingDetails.driver.location.long]} icon={MarkerCar} key="1">
                <Tooltip direction="bottom" offset={[0, 0]} opacity={1} permanent>
                    {trackingDetails.driver.name} #{trackingDetails.driver.id}
                </Tooltip>
            </Marker>
        }
    }


    renderVehiclePath() {
        if(this.props?.hideVehiclePath) {
            return null;
        }
        
        const polylineData = this.props?.gpsPings;
        const positions = polylineData?.map(data => [data.latitude, data.longitude]);
        return <Polyline positions={positions} color={'#424242'} weight={3}></Polyline>
    }


    renderCompletedJobMarker(job, index, customTooltipContent) {
        if(this.props?.hideCompletedJobMarkers) {
            return null;
        }
        
        const lat = job.lat ?? job.latitude;
        const long = job.long ?? job.longitude;
        const iLogixJobNumber = job.iLogixJobNumber ?? '';
        const icon = job.isDelivery ? MarkerGreen : MarkerGrey;
        
        return <Marker 
            position={[lat, long]} 
            icon={icon} 
            key={index} 
            onClick={() => this.emitCompletedJobMarkerClicked(iLogixJobNumber)}
            onMouseover={(e) => {
                const tooltipElem = e.target?.getTooltip().getElement();
                const elems = tooltipElem?.querySelectorAll('div.job-number');
                if(elems?.length) {
                    Object.keys(elems)?.forEach(key => {
                        const elem = elems[key];
                        elem.style.display = 'unset'; /* Display JobNumber on hover */
                    })
                }
            }}
            onMouseout={(e) => {
                const tooltipElem = e.target?.getTooltip()?.getElement();
                const elems = tooltipElem?.querySelectorAll('div.job-number');
                if(elems?.length) {
                    Object.keys(elems)?.forEach(key => {
                        const elem = elems[key];
                        elem.style.display = 'none'; /* hide JobNumber on mouseout */
                    })
                }
            }}
        >
            <Tooltip 
                direction="bottom" 
                offset={[0, 0]} 
                permanent={true}
            >
                {customTooltipContent ? customTooltipContent : this.renderCompletedJobMarkerTooltipContent(job)}
            </Tooltip>
        </Marker>
    }


    renderActiveJobMarker(job, index, customTooltipContent) {
        if(this.props?.hideActiveJobMarkers) {
            return null;
        }
        
        const lat = job.lat ?? job.latitude;
        const long = job.long ?? job.longitude;
        const iLogixJobNumber = job.iLogixJobNumber ?? '';
        
        return <Marker position={[lat, long]} icon={MarkerRed} key={index} onClick={() => this.emitLiveJobMarkerClicked(iLogixJobNumber)}>
            <Tooltip direction="bottom" offset={[0, 0]} opacity={1} permanent>
                {customTooltipContent ? customTooltipContent : this.renderActiveJobMarkerTooltipContent(job, index)}
            </Tooltip>
        </Marker>
    }


    renderPickupLegMarker(leg, index) {
        return <Marker position={[leg.location.lat, leg.location.long]} icon={MarkerGreen} key={index}>
            <Popup closeButton={false}>
                <div>
                    <b>{leg.address}</b>
                </div>
            </Popup>
            <Tooltip direction="bottom" offset={[0, 0]} opacity={1} permanent>
                {leg.legType}
            </Tooltip>
        </Marker>
    }


    renderVehicleMarker(driver, index) {
        if(this.props.hideVehicleMarkers) {
            return null;
        }
        
        return <Marker position={[driver.lat, driver.lng]} icon={MarkerCar} key={index}>
            <Popup closeButton={false} offset={[-8, -5]}>
                <div>
                    <b>{driver.name}</b>
                </div>
            </Popup>
            <Tooltip direction="top" offset={[-5, -25]} opacity={1} permanent>
                #{driver.id}
            </Tooltip>
        </Marker>
    }
    // #endregion


    // #region Methods for rendering tooltip contents
    renderActiveJobMarkerTooltipContent(job, index) {
        const jobNumber = job.shortJobNumber ?? job.number ?? job.jobNumber;
        
        return <div key={index} className="active-job-marker-tooltip-content">
            <div className="flex flex-row gap-1"><b>Job#</b><span>{jobNumber}</span></div>
            {this.renderRouteCodeDetailsInMarker(job)}
        </div>
    }


    renderCompletedJobMarkerTooltipContent(job) {
        const jobNumber = job.shortJobNumber ?? job.jobNumber;
        const deliveryIndicator = job.isDelivery ? '- Delivery' : '';
        
        return <div key={job.uniqueKey} className="completed-job-marker-tooltip-content">
            <div className="flex flex-row gap-1"><b>Drop# {job.dropNumber} {deliveryIndicator}</b></div>
            <div className="job-number"><b>Job#</b> {jobNumber ?? 'N/A'}</div>
            {this.renderRouteCodeDetailsInMarker(job)}
        </div>
    }
    // #endregion
    
    
    /**
     * Render the data inside this.props?.trackingDetails
     */
    renderTrackingDetails() {
        if(!this.props?.trackingDetails) {
            return null;
        }
        
        const trackingDetails = this.props?.trackingDetails;
        const pickupLegs = trackingDetails?.legs?.filter(leg => leg.legType === 'Pickup');

        return <>
            {this.renderActiveJobs(trackingDetails?.driver?.incompleteLegs ?? [])}
            {this.renderCompletedJobs(trackingDetails?.driver?.completeLegs)}
            {this.renderDriverMarker(trackingDetails)}
            {this.renderVehiclePath()}
            {pickupLegs.map((leg, index) => this.renderPickupLegMarker(leg, index))}
        </>
    }


    renderVehicles() {
        if(this.props?.drivers?.length) {
            return this.props?.drivers.map((driver, index) => this.renderVehicleMarker(driver, index))
        }
    }


    renderActiveJobs(activeJobs) {
        if(activeJobs) {
            const groupedByCoords = this.groupByCoordinates(activeJobs);
            
            return groupedByCoords?.map((groupedJobs, groupedJobsIndex) => {
                const job = groupedJobs?.at(0);

                const tooltipContent = <div className="flex flex-column">
                    {groupedJobs?.map((job, jobIndex) => this.renderActiveJobMarkerTooltipContent(job, jobIndex))}
                </div>;
                
                return this.renderActiveJobMarker(job, groupedJobsIndex, tooltipContent);
            }) ?? [];
        }
    }


    renderCompletedJobs(completedJobs) {
        if(completedJobs?.length) {
            const groupedByCoords = this.groupByCoordinates(completedJobs);

            return groupedByCoords?.map((groupedJobs, groupedJobIndex) => {
                const job = groupedJobs?.at(0);

                const tooltipContent = <div className="flex flex-column">
                    {groupedJobs?.map(job => this.renderCompletedJobMarkerTooltipContent(job))}
                </div>;
                
                return this.renderCompletedJobMarker(job, groupedJobIndex, tooltipContent);
            }) ?? [];
        }
    }


    render() {
        const trackingDetails = this.props?.trackingDetails;
        const mapCenter = (trackingDetails) ? this.getInitialMapCenter(trackingDetails) : [this.driversCoordinateProps?.lat, this.driversCoordinateProps?.lng];
        const mapBounds = (trackingDetails) ? this.getBoxTd(trackingDetails) : this.getBox();
        
        return (
            <div className="map-pane-component" style={{ height: '100%' }}>
                {this.renderLegendContainer()}

                <Map ref={(e) => this.mapRef = e} center={mapCenter} bounds={mapBounds} maxZoom={19}>
                    <TileLayer
                        attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
                        url='https://{s}.tile.osm.org/{z}/{x}/{y}.png'
                    />

                    {this.renderTrackingDetails()}
                    {this.renderVehicles()}
                    {this.renderActiveJobs(this.props?.activeJobs ?? [])}
                    {this.renderCompletedJobs(this.props?.completedJobs ?? [])}
                </Map>
            </div>
        );
    }
}